import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Check, X, ExternalLink, Loader2 } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import { AvatarCircle } from '@/components/avatar-circle';
import { ParentProgrammesSection } from '@/features/skillcards/parent-programmes-section';
import {
  useChildrenAccess,
  useMyParentSubscriptions,
  useAssignChildSeat,
  useUnassignChildSeat,
  useSubscriptionCheckout,
  useManageSubscription,
  type ChildAccess,
  type ParentSubscription,
} from '@/lib/parent/subscriptions';
import {
  PARENT_PLANS,
  PARENT_PLAN_LABELS,
  PARENT_PLAN_PRICE,
  PARENT_PLAN_SEATS_LABEL,
  PARENT_PLAN_DESCRIPTION,
  PLAN_LABELS,
  planChildSeatLimit,
  seatsUsedLabel,
  hasFreeSeat,
  freeSeatCount,
  subWithFreeSeat,
  seatErrorMessage,
  type ParentPlan,
} from '@/lib/parent/plan-seats';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function childName(c: ChildAccess): string {
  return c.display_name ?? 'Enfant';
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ParentSubscriptionsPage() {
  const { user } = useAuth();
  const parentId = user?.id ?? '';
  const qc = useQueryClient();

  const { data: subs, isLoading: subsLoading } = useMyParentSubscriptions(parentId);
  const { data: access, isLoading: accessLoading } = useChildrenAccess(parentId);
  const native = isNativeApp();

  // Retour Stripe : ?sub_success=1 → rafraîchir l'état d'accès.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('sub_success') !== '1' || !parentId) return;
    window.history.replaceState({}, '', window.location.pathname);
    qc.invalidateQueries({ queryKey: ['children-access'] });
    qc.invalidateQueries({ queryKey: ['parent-subscriptions'] });
    qc.invalidateQueries({ queryKey: ['parent-children'] });
  }, [parentId, qc]);

  const children = access ?? [];
  const ownedSubs = subs ?? [];
  const isLoading = subsLoading || accessLoading;
  // Tout le monde a déjà un accès : la souscription devient une action
  // secondaire (changer / ajouter une formule), repliée derrière un bouton.
  // Les sièges libres de ses abonnements suffisent aussi : pas besoin d'en
  // racheter un (on attribue en un tap depuis la liste des enfants).
  const uncovered = children.filter((c) => !c.has_access).length;
  const allCovered =
    children.length > 0 && (uncovered === 0 || freeSeatCount(ownedSubs) >= uncovered);
  const [showSubscribe, setShowSubscribe] = useState(false);

  return (
    <div className="pb-safe mx-auto max-w-2xl space-y-6 px-4 py-6 sm:py-8">
      <div>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">Abonnements</h1>
        <p className="text-ink-500 mt-1 font-sans text-sm">Qui a accès à quoi, et tes formules.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : (
        <>
          {/* ── Statut des enfants ──────────────────────────────────────── */}
          {children.length > 0 && <ChildrenStatusList access={children} subs={ownedSubs} />}

          {/* ── Mes abonnements ─────────────────────────────────────────── */}
          {ownedSubs.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-display text-ink-700 text-sm font-bold uppercase tracking-wider">
                Mes abonnements
              </h2>
              {ownedSubs.map((sub) => (
                <OwnedSubscriptionCard
                  key={sub.id}
                  sub={sub}
                  linkedChildren={children}
                  native={native}
                />
              ))}
            </section>
          )}

          {/* ── Souscrire ───────────────────────────────────────────────── */}
          {allCovered && !showSubscribe ? (
            <div className="space-y-2">
              <p className="text-ink-500 font-sans text-sm">
                {uncovered === 0
                  ? 'Tous tes enfants ont un accès complet ✓'
                  : 'Il te reste des places dans ton abonnement : ajoute tes enfants ci-dessus.'}
              </p>
              <button
                type="button"
                onClick={() => setShowSubscribe(true)}
                className="border-ink-200 text-ink-700 hover:border-primary-400 flex min-h-[2.75rem] w-full items-center justify-center rounded-2xl border border-dashed font-sans text-sm font-semibold active:scale-[0.98]"
              >
                Voir les formules
              </button>
            </div>
          ) : (
            <SubscribePanel linkedChildren={children} native={native} parentId={parentId} />
          )}

          {/* ── Programmes de SKILLCARDS par niveau (web : App Store 3.1.1) ── */}
          {!native && <ParentProgrammesSection linkedChildren={children} />}
        </>
      )}
    </div>
  );
}

// ─── Carte d'un abonnement possédé ──────────────────────────────────────────

function OwnedSubscriptionCard({
  sub,
  linkedChildren,
  native,
}: {
  sub: ParentSubscription;
  linkedChildren: ChildAccess[];
  native: boolean;
}) {
  const assign = useAssignChildSeat();
  const unassign = useUnassignChildSeat();
  const manage = useManageSubscription();
  const [selectedChild, setSelectedChild] = useState('');
  const [error, setError] = useState<string | null>(null);

  const limit = planChildSeatLimit(sub.plan);
  const free = hasFreeSeat(sub.seats.length, sub.plan);
  const seatedIds = new Set(sub.seats.map((s) => s.user_id));
  const candidates = linkedChildren.filter((c) => !seatedIds.has(c.child_id));

  async function handleAssign() {
    if (!selectedChild) return;
    setError(null);
    try {
      await assign.mutateAsync({ subscriptionId: sub.id, childId: selectedChild });
      setSelectedChild('');
    } catch (e) {
      setError(seatErrorMessage((e as Error).message));
    }
  }

  async function handleUnassign(childId: string, name: string) {
    if (!confirm(`Retirer ${name} de cet abonnement ? Il perdra son accès.`)) return;
    setError(null);
    try {
      await unassign.mutateAsync({ subscriptionId: sub.id, childId });
    } catch (e) {
      setError(seatErrorMessage((e as Error).message));
    }
  }

  return (
    <div className="border-xp-200 bg-xp-50 space-y-4 rounded-2xl border p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xp-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Abonnement actif
          </p>
          <h3 className="font-display text-ink-900 mt-0.5 text-base font-extrabold">
            {PLAN_LABELS[sub.plan] ?? sub.plan}
          </h3>
        </div>
        <span className="bg-xp-100 text-xp-700 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
          {seatsUsedLabel(sub.seats.length, sub.plan)} enfant{limit > 1 ? 's' : ''}
        </span>
      </div>

      {sub.current_period_end && (
        <p className="text-ink-500 font-sans text-sm">
          {sub.cancel_at_period_end
            ? `Se termine le ${formatDate(sub.current_period_end)}`
            : `Prochain renouvellement le ${formatDate(sub.current_period_end)}`}
        </p>
      )}

      {/* Enfants attribués */}
      {sub.seats.length > 0 && (
        <ul className="space-y-2">
          {sub.seats.map((seat) => (
            <li
              key={seat.user_id}
              className="border-ink-100 flex items-center gap-3 rounded-xl border bg-white p-2.5"
            >
              <AvatarCircle name={seat.display_name ?? 'Enfant'} src={seat.avatar_url} size="sm" />
              <span className="text-ink-800 min-w-0 flex-1 truncate font-sans text-sm font-semibold">
                {seat.display_name ?? 'Enfant'}
              </span>
              <button
                type="button"
                onClick={() => handleUnassign(seat.user_id, seat.display_name ?? 'cet enfant')}
                disabled={unassign.isPending}
                className="text-ink-400 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:text-red-500 disabled:opacity-50"
                aria-label="Retirer"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Attribuer un enfant (autorisé sur natif — ce n'est pas un achat) */}
      <div className="space-y-2">
        {free && candidates.length > 0 ? (
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={selectedChild}
              onChange={(e) => setSelectedChild(e.target.value)}
              className="border-ink-200 text-ink-800 min-h-[2.75rem] flex-1 rounded-xl border bg-white px-3 font-sans text-sm"
            >
              <option value="">Attribuer un enfant…</option>
              {candidates.map((c) => (
                <option key={c.child_id} value={c.child_id}>
                  {childName(c)}
                  {c.has_access ? ' (déjà un accès)' : ''}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              onClick={handleAssign}
              disabled={!selectedChild || assign.isPending}
              className="gap-1.5"
            >
              {assign.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              Attribuer
            </Button>
          </div>
        ) : !free ? (
          <p className="text-ink-400 font-sans text-xs">
            Tous les sièges de cet abonnement sont occupés.
          </p>
        ) : (
          <p className="text-ink-400 font-sans text-xs">
            Tous tes enfants liés sont déjà attribués.
          </p>
        )}
      </div>

      {error && <p className="font-sans text-xs font-semibold text-red-600">{error}</p>}

      <button
        type="button"
        onClick={() => manage.mutate()}
        disabled={manage.isPending}
        className="border-xp-300 text-xp-700 flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-xl border py-2.5 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
      >
        <ExternalLink className="h-4 w-4" />
        {manage.isPending
          ? 'Ouverture…'
          : native
            ? 'Gérer / résilier (Réglages du store)'
            : 'Gérer / changer de plan / résilier'}
      </button>
      {native && (
        <p className="text-ink-400 font-sans text-[11px] leading-relaxed">
          Pour changer de formule, souscris simplement l'autre plan ci-dessous — l'App Store gère le
          changement.
        </p>
      )}
    </div>
  );
}

// ─── Statut d'accès de chaque enfant ────────────────────────────────────────

function ChildrenStatusList({
  access,
  subs,
}: {
  access: ChildAccess[];
  subs: ParentSubscription[];
}) {
  const assign = useAssignChildSeat();
  const [error, setError] = useState<string | null>(null);
  const freeSub = subWithFreeSeat(subs);

  async function addToSub(childId: string) {
    if (!freeSub) return;
    setError(null);
    try {
      await assign.mutateAsync({ subscriptionId: freeSub.id, childId });
    } catch (e) {
      setError(seatErrorMessage((e as Error).message));
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="font-display text-ink-700 text-sm font-bold uppercase tracking-wider">
        Mes enfants
      </h2>
      <ul className="space-y-2">
        {access.map((c) => (
          <li
            key={c.child_id}
            className="border-ink-100 flex items-center gap-3 rounded-2xl border bg-white p-3"
          >
            <AvatarCircle name={childName(c)} src={c.avatar_url} size="sm" />
            <span className="text-ink-800 min-w-0 flex-1 truncate font-sans text-sm font-semibold">
              {childName(c)}
            </span>
            {c.has_access ? (
              <span className="ocean:bg-green-400/15 ocean:text-green-300 shrink-0 rounded-full bg-green-100 px-2.5 py-0.5 font-sans text-xs font-semibold text-green-700">
                {c.source === 'subscription' ? 'Abonné' : 'Pass'}
              </span>
            ) : freeSub ? (
              <Button
                type="button"
                size="sm"
                onClick={() => void addToSub(c.child_id)}
                disabled={assign.isPending}
                className="min-h-[2.75rem] shrink-0 gap-1.5"
                aria-label={`Ajouter ${childName(c)} à ton abonnement`}
              >
                {assign.isPending && assign.variables?.childId === c.child_id ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Check className="h-4 w-4" aria-hidden />
                )}
                Ajouter
              </Button>
            ) : (
              <span className="bg-ink-100 text-ink-500 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
                Sans accès
              </span>
            )}
          </li>
        ))}
      </ul>
      {error && <p className="font-sans text-xs font-semibold text-red-600">{error}</p>}
    </section>
  );
}

// ─── Panneau de souscription (natif = IAP · web = Stripe) ───────────────────

function SubscribePanel({
  linkedChildren,
  native,
  parentId,
}: {
  linkedChildren: ChildAccess[];
  native: boolean;
  parentId: string;
}) {
  const checkout = useSubscriptionCheckout(parentId);
  const [plan, setPlan] = useState<ParentPlan>('family_monthly');
  const [childId, setChildId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Sur natif, l'attribution du siège se fait après l'achat → l'enfant n'est
  // jamais requis. Sur web, l'abonnement Élève exige l'enfant (metadata Stripe).
  const needsChild = !native && plan === 'student_monthly';

  async function handleSubscribe() {
    setError(null);
    setDone(false);
    if (needsChild && !childId) {
      setError('Choisis l’enfant à abonner.');
      return;
    }
    try {
      const res = await checkout.mutateAsync({ plan, childId: childId || null });
      // Web : navigation Stripe (ne revient pas ici). Natif : achat résolu.
      if (res.platform === 'native') setDone(true);
    } catch (e) {
      const msg = (e as Error).message;
      if (msg === 'user_cancelled') return; // achat annulé → silencieux
      setError(nativePurchaseError(msg) ?? seatErrorMessage(msg));
    }
  }

  return (
    <section className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
      <div className="space-y-1">
        <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
          Souscrire
        </p>
        <h2 className="font-display text-ink-900 text-xl font-extrabold">Abonner un enfant</h2>
        <p className="text-ink-500 font-sans text-sm leading-relaxed">
          Choisis une formule ; tu peux changer de plan à tout moment.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {PARENT_PLANS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPlan(p)}
            className={cn(
              'rounded-2xl border-2 p-4 text-left transition-all active:scale-[.98]',
              plan === p ? 'border-primary-400 bg-primary-50' : 'border-ink-200 bg-white',
            )}
          >
            <div className="flex items-baseline justify-between gap-2">
              <p
                className={cn(
                  'font-display text-sm font-bold',
                  plan === p ? 'text-primary-700' : 'text-ink-800',
                )}
              >
                {PARENT_PLAN_LABELS[p]}
              </p>
              <p className="font-display text-ink-900 text-sm font-extrabold">
                {PARENT_PLAN_PRICE[p]}
                <span className="text-ink-400 text-[10px] font-normal"> /mois</span>
              </p>
            </div>
            <p className="text-primary-500 mt-1 font-sans text-[11px] font-semibold">
              {PARENT_PLAN_SEATS_LABEL[p]}
            </p>
            <p className="text-ink-500 mt-1 font-sans text-xs leading-snug">
              {PARENT_PLAN_DESCRIPTION[p]}
            </p>
          </button>
        ))}
      </div>

      {linkedChildren.length > 0 ? (
        <div>
          <label className="text-ink-600 mb-1 block font-sans text-xs font-semibold">
            {needsChild ? 'Rattacher à' : 'Premier enfant (optionnel)'}
          </label>
          <select
            value={childId}
            onChange={(e) => setChildId(e.target.value)}
            className="border-ink-200 text-ink-800 min-h-[2.75rem] w-full rounded-xl border bg-white px-3 font-sans text-sm"
          >
            <option value="">{needsChild ? 'Choisir un enfant…' : 'Aucun pour l’instant'}</option>
            {linkedChildren.map((c) => (
              <option key={c.child_id} value={c.child_id}>
                {childName(c)}
                {c.has_access ? ' (déjà un accès)' : ''}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <p className="text-ink-500 font-sans text-xs leading-relaxed">
          Lie d’abord un enfant à ton compte depuis{' '}
          <span className="font-semibold">Mes enfants</span> pour lui attribuer l’abonnement.
        </p>
      )}

      {error && <p className="font-sans text-xs font-semibold text-red-600">{error}</p>}

      {done && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2">
          <p className="font-sans text-xs font-semibold text-green-800">
            Abonnement enregistré ✓ Attribue tes enfants ci-dessus (l'activation peut prendre
            quelques secondes).
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={handleSubscribe}
        disabled={checkout.isPending}
        className="bg-primary-400 font-display hover:bg-primary-500 min-h-[2.75rem] w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
      >
        {checkout.isPending
          ? native
            ? 'Achat en cours…'
            : 'Redirection…'
          : `S'abonner · ${PARENT_PLAN_LABELS[plan]} · ${PARENT_PLAN_PRICE[plan]}/mois`}
      </button>
      {native && (
        <p className="text-ink-400 text-center font-sans text-[11px]">
          Paiement sécurisé via l'App Store.
        </p>
      )}
    </section>
  );
}

/** Messages FR pour les erreurs d'achat IAP natif (slugs de purchaseNative). */
function nativePurchaseError(msg: string): string | null {
  if (msg.startsWith('product_not_available')) {
    return "Cette formule n'est pas encore disponible sur l'App Store.";
  }
  if (msg === 'no_offering') return 'Aucune offre disponible pour le moment.';
  if (msg === 'revenuecat_not_ready') return 'Paiement indisponible — relance l’app et réessaie.';
  return null;
}
